import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { ErrorToast } from "@/components/ErrorToast";
import { refineErrorMessage } from "@/utils/errorMessages";

/**
 * Toast state interface
 */
interface ToastState {
  isOpen: boolean;
  message: string;
  variant: "error" | "warning" | "info" | "success";
}

/**
 * Context interface
 */
interface ToastContextType {
  showToast: (message: string, variant?: ToastState["variant"]) => void;
  showError: (error: unknown) => void;
  hideToast: () => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

/**
 * Event-based bridge for non-React code (like API client) to trigger toasts
 * This allows the axios interceptor to show errors without being inside React tree
 */
type ToastEventHandler = (message: string, variant: ToastState["variant"]) => void;
let globalToastHandler: ToastEventHandler | null = null;

export function setGlobalToastHandler(handler: ToastEventHandler | null) {
  globalToastHandler = handler;
}

export function triggerGlobalToast(message: string, variant: ToastState["variant"] = "error") {
  if (globalToastHandler) {
    globalToastHandler(message, variant);
  }
}

/**
 * ToastProvider - Wrap your app with this to enable global toast notifications
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState>({
    isOpen: false,
    message: "",
    variant: "error",
  });

  const showToast = useCallback((message: string, variant: ToastState["variant"] = "error") => {
    setToast({ isOpen: true, message, variant });
  }, []);

  const showError = useCallback((error: unknown) => {
    const message = refineErrorMessage(error);
    setToast({ isOpen: true, message, variant: "error" });
  }, []);

  const hideToast = useCallback(() => {
    setToast((prev) => ({ ...prev, isOpen: false }));
  }, []);

  // Register global handler on mount
  useEffect(() => {
    setGlobalToastHandler(showToast);
    return () => setGlobalToastHandler(null);
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, showError, hideToast }}>
      {children}
      <ErrorToast
        isOpen={toast.isOpen}
        onClose={hideToast}
        message={toast.message}
        variant={toast.variant}
      />
    </ToastContext.Provider>
  );
}

/**
 * Hook to use toast notifications in components
 */
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
}
